import React, { useState } from 'react';
import { Button } from '../../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../components/ui/dialog';
import { Switch } from '../../../../components/ui/switch';
import { Trash2, Building2, Calendar, MapPin, AlertTriangle, RotateCcw } from 'lucide-react';
import type { Project } from '../../../project/types/project';

interface ProjectDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  projects: Project[];
  onConfirmDelete: (softDelete: boolean) => void;
  isDeleting?: boolean;
}

export const ProjectDeleteModal: React.FC<ProjectDeleteModalProps> = ({
  isOpen,
  onClose,
  projects,
  onConfirmDelete,
  isDeleting = false
}) => {
  const [isSoftDelete, setIsSoftDelete] = useState(true);

  const closeModal = () => {
    setIsSoftDelete(true);
    onClose();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleConfirm = async () => {
   await onConfirmDelete(isSoftDelete);
    closeModal();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-[800px] p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-shrink-0 w-10 h-10 bg-hero-red-10 rounded-full flex items-center justify-center">
            <Trash2 className="h-5 w-5 text-hero-red" />
          </div>
          <DialogTitle className="font-heading text-[18px] font-semibold text-blueprint-blue leading-tight">
            Delete Projects ({projects.length} project{projects.length !== 1 ? 's' : ''})
          </DialogTitle>
        </div>

        {/* Delete Type Toggle */}
        <div className="mb-4 p-4 bg-page-bg border border-[#E2E5EA] rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-medium text-text-grey">Delete Type:</span>
              <div className="flex items-center gap-2">
                <span className={`text-[13px] ${isSoftDelete ? 'text-blueprint-blue font-medium' : 'text-heritage-grey'}`}>
                  Soft Delete
                </span>
                <Switch
                  checked={!isSoftDelete}
                  onCheckedChange={(checked: boolean) => setIsSoftDelete(!checked)}
                  disabled={isDeleting}
                />
                <span className={`text-[13px] ${!isSoftDelete ? 'text-readable-red font-medium' : 'text-heritage-grey'}`}>
                  Hard Delete
                </span>
              </div>
            </div>
          </div>
          
          {isSoftDelete ? (
            <div className="flex items-start gap-2 p-3 bg-[rgba(0,39,63,0.06)] border border-[#E2E5EA] rounded-lg">
              <RotateCcw className="h-4 w-4 text-blueprint-blue mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[13px] text-blueprint-blue font-medium">Soft Delete (Reversible)</p>
                <p className="text-[12px] text-text-grey mt-1">
                  Projects will be marked as deleted but can be restored later. Data is preserved and can be recovered.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2 p-3 bg-[rgba(196,0,31,0.06)] border border-hero-red-20 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-readable-red mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[13px] text-readable-red font-medium">Hard Delete (Permanent)</p>
                <p className="text-[12px] text-text-grey mt-1">
                  This action cannot be undone. Projects and all associated data will be permanently deleted.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="py-4 max-h-96 overflow-y-auto">
          {projects.length === 0 ? (
            <div className="text-center py-8 text-heritage-grey">
              No projects selected for deletion
            </div>
          ) : (
            <div className="space-y-3">
              {projects.map((project, index) => (
                <div
                  key={project.projectId || index}
                  className="flex items-center justify-between p-4 bg-page-bg rounded-lg hover:bg-[#FAFBFC] transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-8 h-8 bg-[rgba(0,39,63,0.06)] rounded-full flex items-center justify-center">
                      <Building2 className="h-4 w-4 text-blueprint-blue" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-[13px] font-medium text-text-grey truncate">
                          {project.projectName}
                        </p>
                        <span className="text-[12px] text-heritage-grey bg-white border border-[#E2E5EA] px-2 py-1 rounded">
                          #{project.projectNumber}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-[12px] text-heritage-grey">
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          <span>{project.location}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          <span>{formatDate(project.startDate)} - {formatDate(project.endDate)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={closeModal}
            disabled={isDeleting}
            className="flex items-center gap-2"
          >
            Cancel
          </Button>
          <Button
           // variant={isSoftDelete ? "default" : "destructive"}
            onClick={handleConfirm}
            disabled={isDeleting || projects.length === 0}
            className="flex items-center gap-2 bg-hero-red hover:bg-hero-red-60 text-white"
          >
            <Trash2 className="h-4 w-4" />
            {isDeleting 
              ? 'Deleting...' 
              : `${isSoftDelete ? 'Soft Delete' : 'Hard Delete'} ${projects.length} Project${projects.length !== 1 ? 's' : ''}`
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
